import React, { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Grid, Paper, TableCell, TableContainer, TableRow, TextField } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoricalDateRangeFilter from './historical-date-range-filter.component';
import { ResizableProfileTable } from 'components/shared/resizable-profile-table';
import {
    HISTORICAL_COLUMN_DEFINITIONS,
    HISTORICAL_COLUMN_LABEL_KEYS,
    HISTORICAL_COLUMNS,
    HISTORICAL_TABLE_PROFILE_ID,
    HistoricalColumnKey,
} from './historical-order-columns';
import { useTranslation } from 'react-i18next';
import { searchHistoricalOrdersTemplate } from 'apollo/templates/historical-order';
import {
    HISTORICAL_ORDERS_PAGE_SIZE,
    HistoricalOrderListItem,
    HistoricalOrderSearchResult,
    MINIMUM_HISTORICAL_SEARCH_LENGTH,
} from 'models/historical-order';
import { formatCurrency, formatDate } from 'utils/format';
import PageTitle from 'components/shared/page-title/page-title.component';
import Loader from 'components/shared/loader/loader.component';
import InfoText from 'components/shared/info-text.component';
import DelayedLinearProgress from 'components/shared/delayed-linear-progress/delayed-linear-progress.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

/**
 * DD-3687: the Historical orders archive.
 *
 * Search-first by design: the page opens empty and runs nothing until a value
 * is entered. An archive is 100,000+ orders per customer, so listing it by
 * default would be both useless and expensive.
 *
 * One free-text box, not a field picker: the value is matched against order
 * number, patient reference, clinic and lab at once, server-side. Someone
 * looking for "Vast" should not have to decide whether it is a clinic or a lab
 * before they can type it.
 *
 * The search lives in the URL rather than component state or localStorage.
 * That gives "Back to results" for free — the browser's back lands on the same
 * rows — and keeps a patient reference out of browser storage, which the
 * frontend rules forbid for personal data.
 */

const toDateParam = (value: Date | null): string | undefined => (value ? value.toISOString() : undefined);

const fromDateParam = (value: string | null): Date | null => (value ? new Date(value) : null);

const HistoricalOrders: FC = () => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const navigate = useNavigate();
    const { showBoundary } = useDenthubErrorBoundary();
    const [searchParams, setSearchParams] = useSearchParams();

    const activeValue = searchParams.get('value') ?? '';
    const activeDateFrom = fromDateParam(searchParams.get('dateFrom'));
    const activeDateTo = fromDateParam(searchParams.get('dateTo'));

    const [value, setValue] = useState(activeValue);
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([activeDateFrom, activeDateTo]);

    const [runSearch, { data, loading, called, fetchMore }] = useLazyQuery<{
        historicalOrders: HistoricalOrderSearchResult;
    }>(searchHistoricalOrdersTemplate, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        onError: (error) => showBoundary(error),
    });

    // A search already in the URL is re-run on mount, which is what makes
    // "Back to results" land on the same rows rather than an empty page.
    const [hasRestoredSearch, setHasRestoredSearch] = useState(false);
    const hasRestorableSearch =
        activeValue.length >= MINIMUM_HISTORICAL_SEARCH_LENGTH || !!activeDateFrom || !!activeDateTo;
    if (!hasRestoredSearch && hasRestorableSearch) {
        setHasRestoredSearch(true);
        runSearch({
            variables: {
                value: activeValue || undefined,
                dateFrom: toDateParam(activeDateFrom),
                dateTo: toDateParam(activeDateTo),
                limit: HISTORICAL_ORDERS_PAGE_SIZE,
                offset: 0,
            },
        });
    }

    // The floor applies to the text only. A date range carries no regex and is
    // served by its own index, so "everything in 2021" is a valid search with
    // no text at all — what is not valid is asking for neither.
    const trimmedValue = value.trim();
    const isValueTooShort = trimmedValue.length > 0 && trimmedValue.length < MINIMUM_HISTORICAL_SEARCH_LENGTH;
    const hasDateRange = !!dateRange[0] || !!dateRange[1];
    const canSearch = !isValueTooShort && (trimmedValue.length >= MINIMUM_HISTORICAL_SEARCH_LENGTH || hasDateRange);

    const handleSearch = () => {
        if (!canSearch) {
            return;
        }
        const nextParams: Record<string, string> = {};
        if (trimmedValue) {
            nextParams.value = trimmedValue;
        }
        if (dateRange[0]) {
            nextParams.dateFrom = dateRange[0].toISOString();
        }
        if (dateRange[1]) {
            nextParams.dateTo = dateRange[1].toISOString();
        }
        setSearchParams(nextParams);
        setHasRestoredSearch(true);
        runSearch({
            variables: {
                value: trimmedValue || undefined,
                dateFrom: toDateParam(dateRange[0]),
                dateTo: toDateParam(dateRange[1]),
                limit: HISTORICAL_ORDERS_PAGE_SIZE,
                offset: 0,
            },
        });
    };

    const rows = data?.historicalOrders?.rows ?? [];
    const hasMore = data?.historicalOrders?.hasMore ?? false;

    /**
     * Infinite scroll: a sentinel below the table asks for the next page as it
     * comes into view.
     *
     * The merge lives in the Apollo field policy for `historicalOrders`, not
     * here — the frontend rules require paginated results to be merged in the
     * cache policy so every consumer gets the same behaviour.
     */
    const isLoadingMore = useRef(false);
    const loadMore = useCallback(async () => {
        if (!fetchMore || !hasMore || loading || isLoadingMore.current) {
            return;
        }
        isLoadingMore.current = true;
        try {
            await fetchMore({ variables: { offset: rows.length } });
        } finally {
            isLoadingMore.current = false;
        }
    }, [fetchMore, hasMore, loading, rows.length]);

    // `loading` is true for a fetchMore as well as the first search. Gating the
    // table on it would unmount the list — and the sentinel with it — every
    // time another page loads, which stops the scrolling after one page.
    const isFirstLoad = loading && rows.length === 0;
    const isLoadingNextPage = loading && rows.length > 0;

    const sentinelRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMore) {
            return undefined;
        }
        const observer = new IntersectionObserver(
            (entries) => entries[0]?.isIntersecting && loadMore(),
            // Start fetching a little before the sentinel is actually on screen.
            { rootMargin: '200px' },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loadMore]);
    const hasSearched = called && hasRestoredSearch;

    const openOrder = (historicalOrder: HistoricalOrderListItem) => {
        // The search stays on this page's URL, so the browser's own back returns
        // to the same results. It is deliberately not copied onto the detail
        // URL: it would put a patient reference in a second address for nothing.
        navigate(`/historical-orders/${historicalOrder._id}`);
    };

    return (
        <Fragment>
            <PageTitle title={translate('orders:HISTORICAL_ORDERS')} headingStyle="h1" />

            <Box
                sx={{
                    marginTop: 1,
                    marginBottom: 2,
                    bgcolor: 'background.paper',
                    padding: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Grid container rowSpacing={1.5} columnSpacing={1}>
                    <Grid item md={5} sm={6} xs={12}>
                        <TextField
                            fullWidth
                            id="historical-orders-filter-value"
                            label={translate('orders:HISTORICAL_SEARCH_FIELD')}
                            placeholder={translate('orders:HISTORICAL_SEARCH_PLACEHOLDER')}
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                            onKeyDown={(event) => event.key === 'Enter' && handleSearch()}
                            error={value.length > 0 && isValueTooShort}
                            helperText={
                                value.length > 0 && isValueTooShort
                                    ? translate('orders:HISTORICAL_SEARCH_MIN_LENGTH', {
                                          count: MINIMUM_HISTORICAL_SEARCH_LENGTH,
                                      })
                                    : undefined
                            }
                            data-test="historical-orders.value-input"
                        />
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <HistoricalDateRangeFilter dateRange={dateRange} onDateRangeChange={setDateRange} />
                    </Grid>
                    {/* The order review filter's action, verbatim: its own
                        right-aligned row, text button, refresh icon. */}
                    <Grid item xs={12} sx={{ textAlign: 'right' }}>
                        <Button
                            onClick={handleSearch}
                            size="medium"
                            type="button"
                            startIcon={<RefreshIcon />}
                            disabled={!canSearch || loading}
                            data-test="historical-orders.search-button"
                        >
                            {translate('common:REFRESH_LIST')}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {isFirstLoad && <Loader />}

            {!isFirstLoad && !hasSearched && (
                <Box mt={3}>
                    <InfoText text={translate('orders:HISTORICAL_SEARCH_TO_BEGIN')} />
                </Box>
            )}

            {!loading && hasSearched && rows.length === 0 && (
                <Box mt={3}>
                    <InfoText text={translate('orders:HISTORICAL_NO_RESULTS')} />
                </Box>
            )}

            {rows.length > 0 && (
                <Box mt={3}>
                    <TableContainer component={Paper}>
                        <ResizableProfileTable<HistoricalOrderListItem, HistoricalColumnKey>
                            profileId={HISTORICAL_TABLE_PROFILE_ID}
                            initialWidths={{}}
                            onPersistWidths={() => undefined}
                            columns={HISTORICAL_COLUMN_DEFINITIONS}
                            activeColumns={HISTORICAL_COLUMNS}
                            items={rows}
                            getItemKey={(historicalOrder) => historicalOrder._id}
                            renderHeaderLabel={(columnKey) => translate(HISTORICAL_COLUMN_LABEL_KEYS[columnKey])}
                            renderRow={({ item: historicalOrder, index: rowIndex }) => (
                                <TableRow
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => openOrder(historicalOrder)}
                                    data-test={`historical-orders.row-${rowIndex}`}
                                >
                                    <TableCell>
                                        {historicalOrder.orderDate
                                            ? formatDate(new Date(historicalOrder.orderDate), 'date')
                                            : ''}
                                    </TableCell>
                                    <TableCell>{historicalOrder.orderNumber}</TableCell>
                                    <TableCell>{historicalOrder.customerName}</TableCell>
                                    <TableCell>{historicalOrder.labName}</TableCell>
                                    <TableCell>{historicalOrder.patientReference}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {formatCurrency(
                                            historicalOrder.totals?.inclVat ?? 0,
                                            historicalOrder.totals?.currency,
                                        )}
                                    </TableCell>
                                    {/* Lines up with the spacer column the table's header renders. */}
                                    <TableCell />
                                </TableRow>
                            )}
                        />
                    </TableContainer>
                    {/* The observer watches this; it is also where the spinner for
                        the next page appears, so the list does not jump. */}
                    <Box ref={sentinelRef} mt={1} display="flex" justifyContent="center">
                        {isLoadingNextPage ? <DelayedLinearProgress /> : undefined}
                    </Box>
                    {!hasMore && rows.length > 0 && (
                        <Box mt={1}>
                            <InfoText text={translate('orders:HISTORICAL_ALL_RESULTS', { count: rows.length })} />
                        </Box>
                    )}
                </Box>
            )}
        </Fragment>
    );
};

export default HistoricalOrders;
